/**
 * DebugContext — provides debug overlay state to the view system.
 *
 * Visible to anyone in non-production and to admins in production.
 * Activated via ?debug=1 URL param.
 * Shows rendering metadata (artifactCard, field slots, actions) on each card/bundle/detail.
 */

import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, Suspense } from 'react';
import type { ViewItem, ViewBundle } from '../../../../../src/shared/view-system/types';
import { InspectorDrawer } from './InspectorDrawer';
import { DevToolbar, ToolbarButton } from './DevToolbar';
import { isProduction } from '../../lib/environment';
import { useDevRecorder } from '../../hooks/useDevRecorder';
import { useUserControlsContext } from '../../contexts/UserControlsContext';
import { useExternalWrites } from './useExternalWrites';
import { ExternalWritesPopover } from './ExternalWritesPopover';
import { describeLeases } from '@shared/external-writes/describe';

const SkillDebugHUD = React.lazy(() => import('../../components/skills/SkillDebugHUD'));
const ReplayOverlay = React.lazy(() => import('./ReplayOverlay').then(m => ({ default: m.ReplayOverlay })));


interface DebugContextValue {
  enabled: boolean;
  toggle: () => void;
  inspectedItem: ViewItem | null;
  inspectedBundle: ViewBundle | null;
  inspectItem: (item: ViewItem) => void;
  inspectBundle: (bundle: ViewBundle) => void;
  closeInspector: () => void;
  wiringTarget: string | null;
  inspectWiring: (item: ViewItem) => void;
}

const DebugContext = createContext<DebugContextValue>({
  enabled: false,
  toggle: () => {},
  inspectedItem: null,
  inspectedBundle: null,
  inspectItem: () => {},
  inspectBundle: () => {},
  closeInspector: () => {},
  wiringTarget: null,
  inspectWiring: () => {},
});

export function useDebugMode(): DebugContextValue {
  return useContext(DebugContext);
}

interface DebugProviderProps {
  children: React.ReactNode;
}

export function DebugProvider({ children }: DebugProviderProps) {
  const [sseHudVisible, setSseHudVisible] = useState(false);
  const [replayVisible, setReplayVisible] = useState(false);
  const recorder = useDevRecorder();

  // Dev chrome is visible in non-prod, or to admins in prod.
  const { isAdmin } = useUserControlsContext();
  const isProd = isProduction();
  const showToolbar = !isProd || isAdmin;

  const initialEnabled = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  }, []);

  const { state: externalWrites } = useExternalWrites({ sync: false });
  const [externalWritesOpen, setExternalWritesOpen] = useState(false);
  const externalWritesChip = useMemo(() => {
    if (externalWrites === 'unknown') {
      return { label: '\u2026', color: 'amber' as const, title: 'External write state unknown' };
    }
    const described = describeLeases(externalWrites, new Date());
    if (described.tone === 'red') return { label: 'LIVE', color: 'red' as const, title: described.headline };
    if (described.tone === 'amber') return { label: 'PARTIAL', color: 'amber' as const, title: described.headline };
    return { label: 'SANDBOX', color: 'green' as const, title: described.headline };
  }, [externalWrites]);

  const [enabled, setEnabled] = useState(initialEnabled);
  const [inspectedItem, setInspectedItem] = useState<ViewItem | null>(null);
  const [inspectedBundle, setInspectedBundle] = useState<ViewBundle | null>(null);
  const [wiringTarget, setWiringTarget] = useState<string | null>(null);

  // Clicking a card badge opens the full drawer (wiring + field map)
  const inspectItem = useCallback((item: ViewItem) => {
    setInspectedItem(item);
    setInspectedBundle(null);
    setWiringTarget(item.id);
  }, []);

  // Alias — kept for DebugBadge API compat, does the same thing
  const inspectWiring = inspectItem;

  const inspectBundle = useCallback((bundle: ViewBundle) => {
    setInspectedBundle(bundle);
    setInspectedItem(null);
    setWiringTarget(null);
  }, []);

  const closeInspector = useCallback(() => {
    setInspectedItem(null);
    setInspectedBundle(null);
    setWiringTarget(null);
  }, []);

  // Escape key closes inspector
  useEffect(() => {
    if (!inspectedItem && !inspectedBundle && !wiringTarget) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeInspector();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inspectedItem, inspectedBundle, wiringTarget, closeInspector]);

  const toggle = useCallback(() => setEnabled((prev) => !prev), []);

  const contextValue = useMemo<DebugContextValue>(
    () => ({
      enabled,
      toggle,
      inspectedItem,
      inspectedBundle,
      inspectItem,
      inspectBundle,
      closeInspector,
      wiringTarget,
      inspectWiring,
    }),
    [
      enabled,
      toggle,
      inspectedItem,
      inspectedBundle,
      inspectItem,
      inspectBundle,
      closeInspector,
      wiringTarget,
      inspectWiring,
    ],
  );

  if (!showToolbar) {
    return <>{children}</>;
  }

  return (
    <DebugContext.Provider value={contextValue}>
      {children}
      <InspectorDrawer
        inspectedItem={inspectedItem}
        inspectedBundle={inspectedBundle}
        wiringTarget={wiringTarget}
        onClose={closeInspector}
        onInspectItem={inspectItem}
      />
      <DevToolbar>
        <ToolbarButton active={enabled} activeColor="amber" onClick={toggle} title="Toggle debug overlay">
          {enabled ? 'DEBUG ON' : 'DEBUG'}
        </ToolbarButton>
        {!isProd && (
          <ToolbarButton
            active
            activeColor={externalWritesChip.color}
            onClick={() => setExternalWritesOpen((v) => !v)}
            title={externalWritesChip.title}
          >
            {externalWritesChip.label}
          </ToolbarButton>
        )}
        <ToolbarButton
          active={sseHudVisible}
          activeColor="indigo"
          onClick={() => setSseHudVisible(v => !v)}
          title="Toggle Agent Inspector (Cmd+Shift+D)"
        >
          Inspector
        </ToolbarButton>
        <RecorderControls recorder={recorder} onReplay={() => setReplayVisible(true)} />
      </DevToolbar>
      {!isProd && externalWritesOpen && <ExternalWritesPopover onClose={() => setExternalWritesOpen(false)} />}
      {sseHudVisible && (
        <Suspense fallback={null}>
          <SkillDebugHUD />
        </Suspense>
      )}
      {replayVisible && recorder.events.length > 0 && (
        <Suspense fallback={null}>
          <ReplayOverlay events={recorder.events} onClose={() => setReplayVisible(false)} />
        </Suspense>
      )}
    </DebugContext.Provider>
  );
}

/**
 * RecorderControls — toolbar pills for rrweb recording.
 *
 * idle     → "Record" pill
 * recording → "REC 0:32" with pulsing red dot (click to stop)
 * recorded  → "Export" + "Replay" pills, small "×" to discard
 */
function RecorderControls({
  recorder,
  onReplay,
}: {
  recorder: ReturnType<typeof useDevRecorder>;
  onReplay: () => void;
}) {
  if (recorder.state === 'recording') {
    const mins = Math.floor(recorder.elapsed / 60);
    const secs = recorder.elapsed % 60;
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;
    return (
      <button
        type="button"
        onClick={recorder.stop}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
        title="Stop recording"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
        REC {timeStr}
      </button>
    );
  }

  if (recorder.state === 'recorded') {
    return (
      <>
        <ToolbarButton active={false} onClick={recorder.exportRecording} title="Download recording as JSON">
          Export
        </ToolbarButton>
        <ToolbarButton active activeColor="indigo" onClick={onReplay} title="Replay recording">
          Replay
        </ToolbarButton>
        <button
          type="button"
          onClick={recorder.reset}
          className="flex h-5 w-5 items-center justify-center rounded-full text-nightshade-500 hover:text-nightshade-200 hover:bg-nightshade-800 text-[11px] font-bold transition-colors"
          title="Discard recording"
        >
          &times;
        </button>
      </>
    );
  }

  // idle
  return (
    <ToolbarButton active={false} onClick={() => { void recorder.start(); }} title="Start rrweb recording">
      Record
    </ToolbarButton>
  );
}

